/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { fireAndForget } from '../../../../helpers/jsHelpers/promise';
import { IMAGE_HEIGHT, IMAGE_WIDTH } from '../../../../helpers/ui/constants';
import { Luminosity } from '../../../../helpers/ui/theme';
import { ignoreIfAborted } from '../../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { getImageUrl } from '../../../../redux/netgemApi/actions/v8/metadataImage';
import './Channel.css';

type ChannelPropType = {
  +'aria-hidden'?: boolean,
  +id: string,
  +imageId: ?string,
  +isUIDisabled: boolean,
  +name: string,
  +onClick: (id: string) => void,
};

const Channel = (channelProps: ChannelPropType): React.Node => {
  const { 'aria-hidden': ariaHidden, id, imageId, isUIDisabled, name, onClick } = channelProps;
  const dispatch = useDispatch();

  const abortController = useRef<AbortController>(new AbortController());

  const [displayChannelName, setDisplayChannelName] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Only contains a cleanup function
  useEffect(() => () => abortController.current.abort('Component ConflictItem will unmount'), []);

  useEffect(() => {
    const loadImage = async () => {
      const { signal } = abortController.current;

      if (!imageId) {
        // Channel (probably a FAST one) without image: display its name
        setDisplayChannelName(true);
        return;
      }

      try {
        const newImageUrl: string = await dispatch(
          getImageUrl(
            {
              assetId: imageId,
              height: IMAGE_HEIGHT.Channel,
              luminosity: Luminosity.Light,
              width: IMAGE_WIDTH.Channel,
            },
            signal,
          ),
        );

        signal.throwIfAborted();

        if (newImageUrl !== '') {
          setImageUrl(newImageUrl);
        } else {
          // 404 HTTP code is generally not considered as an error for images but in the particular context of a channel image in the zapper, it is
          setDisplayChannelName(true);
        }
      } catch (error) {
        await ignoreIfAborted(signal, error);

        // Error loading channel image: display its name
        setDisplayChannelName(true);
      }
    };

    fireAndForget(loadImage(), 'ChannelView.loadImage');
  }, [imageId]);

  const handleOnClick = useCallback(() => {
    onClick(id);
  }, [id, onClick]);

  return (
    <div aria-hidden={ariaHidden} aria-label={name} className={clsx('channel', isUIDisabled && 'disabled')} onClick={handleOnClick}>
      {imageUrl ? <img alt='' aria-hidden className='channelImage' draggable={false} src={imageUrl} /> : null}
      {displayChannelName ? <div className='channelName'>{name}</div> : null}
    </div>
  );
};

export default Channel;
